import { useMemo, useState, type FormEvent } from "react";
import { Activity, Database, Pause, Play, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import type { DataSource } from "../../shared/types";
import { Badge, Button, Card, SectionHeader, Spinner } from "../components/Ui";
import { api } from "../lib/api";
import { useData } from "../lib/data";

function formatDate(value?: string) {
  if (!value) return "尚未检查";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function statusLabel(status: DataSource["status"]) {
  if (status === "connected") return "可用";
  if (status === "degraded") return "降级";
  return "已停用";
}

export function SourcesPage() {
  const { data, refresh } = useData();
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("内部研究资料");
  const [coverage, setCoverage] = useState("");
  const [priority, setPriority] = useState(60);
  const [trustLevel, setTrustLevel] = useState<DataSource["trustLevel"]>("内部资料");
  const [error, setError] = useState("");
  const sources = data?.sources ?? [];
  const connected = sources.filter((item) => item.status === "connected").length;
  const official = sources.filter((item) => item.trustLevel === "官方" && item.status === "connected").length;
  const coverageTypes = useMemo(() => new Set(sources.filter((item) => item.status !== "paused").map((item) => item.type)).size, [sources]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api.saveSource({ name, type, coverage, priority, trustLevel, status: "paused", provider: "机构自定义", note: "等待数据管理员完成授权和字段映射" });
      setName(""); setCoverage(""); setOpen(false);
      await refresh();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "资料源保存失败"); }
  };

  const check = async (source: DataSource) => {
    setChecking(source.id); setError("");
    try { await api.checkSource(source.id); await refresh(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "连接检查失败"); }
    finally { setChecking(""); }
  };

  const toggle = async (source: DataSource) => {
    await api.updateSource(source.id, { status: source.status === "paused" ? "connected" : "paused" });
    await refresh();
  };

  return <div className="page-stack sources-v2">
    <div className="page-title"><div><span className="eyebrow">DATA GOVERNANCE</span><h1>数据覆盖与健康度</h1><p>只展示系统真正使用或准备接入的数据源；虚构的“Wind 已连接”已经移除。</p></div><Button onClick={() => setOpen(!open)}><Plus size={16}/>登记内部资料源</Button></div>

    <div className="source-metrics"><Card><span>当前可用</span><strong>{connected}/{sources.length}</strong><small>公开端点或模型服务</small></Card><Card><span>官方证据源</span><strong>{official}</strong><small>公告、指数和利率基准</small></Card><Card><span>覆盖类型</span><strong>{coverageTypes}</strong><small>行情 / 新闻 / 披露 / 基准 / AI</small></Card><Card><span>治理原则</span><strong>来源优先</strong><small>AI 不替代事实源</small></Card></div>

    {open && <Card><form className="source-register-form" onSubmit={submit}><div><strong>登记机构内部资料源</strong><span>登记不会自动上传文件或连接内网；默认保持停用，等待授权和字段映射。</span></div><label>名称<input required value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：内部路演纪要库"/></label><label>类型<select value={type} onChange={(event) => setType(event.target.value)}><option>内部研究资料</option><option>持仓与估值表</option><option>合规规则库</option><option>自定义数据接口</option></select></label><label>覆盖范围<input required value={coverage} onChange={(event) => setCoverage(event.target.value)} placeholder="覆盖哪些资产、字段或报告"/></label><label>信任级别<select value={trustLevel} onChange={(event) => setTrustLevel(event.target.value as DataSource["trustLevel"])}><option>内部资料</option><option>官方</option><option>主要财经媒体</option><option>公开市场数据</option></select></label><label>研究优先级<input type="number" min="1" max="100" value={priority} onChange={(event) => setPriority(Number(event.target.value))}/></label><Button type="submit">保存为待接入</Button></form></Card>}

    {error && <div className="form-error">{error}</div>}
    <Card>
      <SectionHeader title="生产数据链路" subtitle="优先级决定 AI 上下文中的引用顺序；暂停后不再作为推荐证据源。" action={<Badge tone="neutral"><ShieldCheck size={13}/>可审计</Badge>}/>
      <div className="source-health-list">{sources.map((source) => <article key={source.id} className={`source-health-row status-${source.status}`}>
        <div className="source-health-icon"><Database size={18}/><i/></div>
        <div className="source-health-main"><div><strong>{source.name}</strong><Badge tone={source.status === "connected" ? "up" : source.status === "degraded" ? "down" : "neutral"}>{statusLabel(source.status)}</Badge><span>{source.trustLevel}</span></div><p>{source.coverage || source.type}</p><small>{source.provider || "机构自定义"} · 优先级 {source.priority ?? 50} · 最近检查 {formatDate(source.checkedAt || source.lastSync)}</small>{source.note && <em>{source.note}</em>}</div>
        <div className="source-health-actions"><Button className="secondary small" onClick={() => void check(source)} disabled={checking === source.id}>{checking === source.id ? <Spinner label="检查中"/> : <><RefreshCw size={13}/>检查连接</>}</Button><button className="icon-button" title={source.status === "paused" ? "启用" : "暂停"} onClick={() => void toggle(source)}>{source.status === "paused" ? <Play size={15}/> : <Pause size={15}/>}</button>{source.provider === "机构自定义" && <button className="icon-button danger" title="删除" onClick={async () => { await api.deleteSource(source.id); await refresh(); }}><Trash2 size={15}/></button>}</div>
      </article>)}</div>
    </Card>

    <Card className="source-routing-card"><SectionHeader title="数据如何进入工作流" subtitle="每类来源只承担它擅长的任务。" action={<Activity size={16}/>}/><div><span><b>行情与新闻</b>投研驾驶舱、投委会近两周证据</span><span><b>官方披露</b>公司与行业一页纸的事实层</span><span><b>官方基准</b>宏观环境和组合校准参数</span><span><b>DeepSeek</b>结构化、反证、会议总结和复盘初稿</span><span><b>内部资料</b>只有完成授权后才能进入 AI 上下文</span></div></Card>
  </div>;
}
